import { inject, Injectable } from "@angular/core";
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext} from "@ngxs/store";
import { toStreamerFollers } from "../../mapper/streamer-follow.mapper";
import { StreamerFollow } from "../../model/streamer-follow.model";
import { StreamerFollowService } from "../../service/streamer-follow.service";
import {
  LoadStreamerFollowers,
  ReloadStreamerFollowers,
  ClearStreamerFollowers} from "./streamer-followers.action";

export class StreamerFollowersStateModel {
  page: number;
  pageSize: number;
  total: number;
  pages: Record<number, StreamerFollow[]>;
  loading: boolean;
  error: Error | undefined;
}

@State<StreamerFollowersStateModel>({
  name: "streamerFollow",
  defaults: {
    page: 1,
    pageSize: 10,
    total: 0,
    pages: {},
    loading: false,
    error: undefined}})
@Injectable()
export class StreamerFollowersState {
  streamerFollowService = inject(StreamerFollowService);

  @Selector()
  static isLoading(state: StreamerFollowersStateModel) {
    return state.loading;
  }

  @Selector()
  static hasError(state: StreamerFollowersStateModel) {
    return !!state.error;
  }

  @Selector()
  static currentPageData(state: StreamerFollowersStateModel) {
    return state.pages[state.page] || [];
  }

  static pageData(page: number) {
    return createSelector(
      [StreamerFollowersState],
      (state: StreamerFollowersStateModel) => state.pages[page] || [],
    );
  }

  @Selector()
  static pagination(state: StreamerFollowersStateModel) {
    return {
      page: state.page,
      pageSize: state.pageSize,
      total: state.total,
      totalPages: Math.ceil(state.total / state.pageSize)};
  }

  @Action(LoadStreamerFollowers)
  async loadStreamerFollowers(
    ctx: StateContext<StreamerFollowersStateModel>,
    action: LoadStreamerFollowers,
  ) {
    const state = ctx.getState();
    const nextPage = action.page ?? state.page;
    const nextPageSize = action.pageSize ?? state.pageSize;
    const key = nextPage;
    if (state.pages[nextPage]) {
      ctx.patchState({
        page: nextPage,
        pageSize: nextPageSize,
        loading: false,
        error: undefined});
      return;
    }
    ctx.patchState({
      loading: true,
      page: nextPage,
      pageSize: nextPageSize,
      error: undefined});

    try {
      const response = await this.streamerFollowService.followers(
        nextPage,
        nextPageSize,
      );

      const follows = toStreamerFollers(response);
      const total = response.pagination.total;

      const current = ctx.getState();
      ctx.patchState({
        loading: false,
        total,
        pages: {
          ...current.pages,
          [key]: follows}});
    } catch (error) {
      console.error("Erro ao carregar dados", error);
      ctx.patchState({
        loading: false,
        error: error as Error});
    }
  }

  @Action(ReloadStreamerFollowers)
  async reloadStreamerFollowers(
    ctx: StateContext<StreamerFollowersStateModel>,
  ) {
    ctx.patchState({
      page: 1,
      total: 0,
      pages: {},
      loading: true,
      error: undefined});
    ctx.dispatch(new LoadStreamerFollowers(1, 10));
  }

  @Action(ClearStreamerFollowers)
  clearStreamerFollowers(ctx: StateContext<StreamerFollowersStateModel>) {
    ctx.patchState({
      page: 1,
      pageSize: 10,
      total: 0,
      pages: {},
      loading: true,
      error: undefined});
  }
}
